import type { LscVodCandidate } from './types';

export type AvailabilityFilterMode = 'all' | 'available' | 'unavailable';

export type VodAvailabilityFields = Pick<
  LscVodCandidate,
  'candidate_status' | 'availability_status' | 'viewable' | 'url'
>;

/** True when a VOD should be hidden from default commissioner review workflows. */
export function isVodCandidateUnavailable(candidate: VodAvailabilityFields): boolean {
  const candidateStatus = String(candidate.candidate_status ?? 'candidate')
    .trim()
    .toLowerCase();
  if (candidateStatus === 'unavailable' || candidateStatus === 'expired') {
    return true;
  }

  const availabilityStatus = String(candidate.availability_status ?? '')
    .trim()
    .toLowerCase();
  if (availabilityStatus === 'unavailable') {
    return true;
  }

  const viewable = (candidate.viewable ?? '').trim().toLowerCase();
  if (viewable && viewable !== 'public') {
    return true;
  }

  return false;
}

export function isVodCandidateAvailableForReview(candidate: VodAvailabilityFields): boolean {
  return !isVodCandidateUnavailable(candidate);
}

export function countUnavailableVodCandidates<T extends VodAvailabilityFields>(
  candidates: T[]
): number {
  return candidates.filter(isVodCandidateUnavailable).length;
}

export function filterVodCandidatesByAvailability<T extends VodAvailabilityFields>(
  candidates: T[],
  mode: AvailabilityFilterMode
): T[] {
  if (mode === 'all') {
    return candidates;
  }
  if (mode === 'unavailable') {
    return candidates.filter(isVodCandidateUnavailable);
  }
  return candidates.filter((candidate) => !isVodCandidateUnavailable(candidate));
}

export const AVAILABILITY_FILTER_LABELS: Record<AvailabilityFilterMode, string> = {
  available: 'Available VODs',
  unavailable: 'Unavailable on Twitch',
  all: 'All VODs',
};
